import { CHAT_COMMAND_ERRORS, type ChatCommandError, WHATSAPP_TEMPLATE_BLOCK_REASONS } from 'cpool-constants';
import { decodeUnknownVariablesBlockReason } from 'cpool-utils';
import type { ParseKeys, TFunction } from 'i18next';
import { toast } from 'react-toastify';

type TranslationKey = ParseKeys<'de'>;

function commandErrorResponse(error: unknown): string | undefined {
    if (error && typeof error === 'object' && 'response' in error && typeof error.response === 'string') {
        return error.response;
    }
    return undefined;
}

function isChatCommandError(code: string): code is ChatCommandError {
    return (Object.values(CHAT_COMMAND_ERRORS) as Array<string>).includes(code);
}

function isWhatsAppTemplateBlockReason(
    code: string,
): code is (typeof WHATSAPP_TEMPLATE_BLOCK_REASONS)[keyof typeof WHATSAPP_TEMPLATE_BLOCK_REASONS] {
    return (Object.values(WHATSAPP_TEMPLATE_BLOCK_REASONS) as Array<string>).includes(code);
}

/**
 * Toasts a translated, code-specific message for a WhatsApp chat command failure when the
 * backend threw a known `CHAT_COMMAND_ERRORS` / `WHATSAPP_TEMPLATE_BLOCK_REASONS` code as the
 * error body (see `APICommunicator.sendCommand`), falling back to a generic toast otherwise
 * (unknown codes, client-side errors, network failures, etc.).
 */
export function toastChatCommandError(error: unknown, t: TFunction, fallbackKey: TranslationKey): void {
    const code = commandErrorResponse(error);

    const unknownVariableNames = code ? decodeUnknownVariablesBlockReason(code) : undefined;
    if (unknownVariableNames) {
        toast.error(
            t('WHATSAPP_CHAT.TEMPLATE_MODAL.BLOCK_REASON.UNKNOWN_VARIABLES', {
                variables: unknownVariableNames.join(', '),
            }),
        );
        return;
    }

    if (code && isWhatsAppTemplateBlockReason(code)) {
        toast.error(t(`WHATSAPP_CHAT.TEMPLATE_MODAL.BLOCK_REASON.${code}`));
        return;
    }

    if (code && isChatCommandError(code)) {
        toast.error(t(`WHATSAPP_CHAT.ERROR.${code}`));
        return;
    }

    toast.error(t(fallbackKey));
}
